import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { CREATE_STEPS, type CreateStepKey } from "./createSteps";
import { CreateStepper } from "./CreateStepper";
import type { CreateWizard } from "./useCreateWizard";

const COLLAPSED_KEY = "invoiceCreate.overviewCollapsed";

const readCollapsed = () => {
    try {
        return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
        return false;
    }
};

const writeCollapsed = (value: boolean) => {
    try {
        localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
    } catch {
        /* private mode / blocked storage — the toggle still works for this visit */
    }
};

/**
 * Top of the creation page: the step bar plus one card per step — filled in for done steps
 * (with "Ändern"), a placeholder outline for the current one, dashed for steps still to come.
 * Folds into a small step bar; the choice is remembered per browser.
 */
export function CreateOverview({
    wizard,
    summaries,
}: {
    wizard: CreateWizard;
    /** What each done step's card shows. Steps without a summary fall back to "Erledigt". */
    summaries: Partial<Record<CreateStepKey, ReactNode>>;
}) {
    const { t } = useTranslation();
    const [collapsed, setCollapsed] = useState(readCollapsed);

    const toggle = () => {
        setCollapsed((c) => {
            writeCollapsed(!c);
            return !c;
        });
    };

    // Before any step is done every card is empty — they'd only push the first step's content
    // (the patient search) below the fold, so they appear once there's something to summarise.
    const anyDone = CREATE_STEPS.some((_, i) => wizard.stateOf(i) === "done");
    const currentLabel = t(`invoiceCreate.steps.${CREATE_STEPS[wizard.current].key}`);
    const position = t("invoiceCreate.stepOf", { current: wizard.current + 1, total: CREATE_STEPS.length });

    if (collapsed) {
        return (
            <div className="ic-overview-pill">
                <CreateStepper wizard={wizard} compact />
                <span className="ic-overview-pill__label">
                    <strong>{currentLabel}</strong> · {position}
                </span>
                <button type="button" className="ic-overview-pill__toggle" onClick={toggle} aria-expanded={false} aria-label={t("invoiceCreate.overviewExpand")} title={t("invoiceCreate.overviewExpand")}>
                    <TbChevronUp />
                </button>
            </div>
        );
    }

    return (
        <section className="ic-overview" aria-label={t("invoiceCreate.overview")}>
            <div className="ic-overview__stepper">
                <CreateStepper wizard={wizard} />
            </div>
            {/* Phones: the five cards don't fit — the compact stepper + current step name says the same. */}
            <div className="ic-overview__mobile-position">
                <CreateStepper wizard={wizard} compact />
                <span>
                    <strong>{currentLabel}</strong> · {position}
                </span>
            </div>

            {anyDone && <div className="ic-overview__cards">
                {CREATE_STEPS.map(({ key }, i) => {
                    const state = wizard.stateOf(i);
                    const label = t(`invoiceCreate.steps.${key}`);
                    if (state === "done") {
                        return (
                            <div key={key} className="ic-card ic-card--done">
                                <span className="ic-card__title">{label}</span>
                                <div className="ic-card__body">{summaries[key] ?? t("invoiceCreate.summaryDone")}</div>
                                <button type="button" className="ic-card__action" onClick={() => wizard.goTo(i)}>
                                    {t("invoiceCreate.change")}
                                </button>
                            </div>
                        );
                    }
                    if (state === "current") {
                        return (
                            <div key={key} className="ic-card ic-card--current" aria-current="step">
                                <span className="ic-card__title">{label}</span>
                                <div className="ic-card__skeleton" aria-hidden>
                                    <span />
                                    <span />
                                    <span />
                                </div>
                                <span className="ic-card__hint">{t("invoiceCreate.summaryCurrent")}</span>
                            </div>
                        );
                    }
                    return (
                        <div key={key} className="ic-card ic-card--upcoming">
                            <span className="ic-card__title">{label}</span>
                        </div>
                    );
                })}
            </div>}

            <div className="ic-overview__bar">
                <span className="ic-overview__bar-title">{t("invoiceCreate.overview")}</span>
                <button type="button" className="ic-overview__toggle" onClick={toggle} aria-expanded>
                    {t("invoiceCreate.overviewCollapse")} <TbChevronDown />
                </button>
            </div>
        </section>
    );
}
